import { useKeyModificationDialogStore } from 'components/WorkspaceEditor/components/KeyModificationWarningDialog'
import { KEY_MODIFICATION_WARNING_DISMISSED_KEY } from 'components/WorkspaceEditor/constants/keyModification'
import { useCallback, useEffect, useRef } from 'react'
import { usePreviewFeatures } from 'shared/hooks/usePreviewFeatures'
import { hasKeyChanged } from '../utils/keyChangeDetection'

/**
 * Hook to handle key change confirmation dialog
 * Shows a confirmation dialog when a keyed text's key portion is modified
 */
export const useKeyChangeConfirmation = () => {
    const { previewFeatures } = usePreviewFeatures()
    const { showDialog, closeDialog } = useKeyModificationDialogStore(
        (state) => ({
            showDialog: state.showDialog,
            closeDialog: state.closeDialog,
        })
    )

    // Store the resolve function for the promise
    const resolveRef = useRef<((value: boolean) => void) | null>(null)

    // Cleanup on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            // If component unmounts while dialog is open, resolve with false (cancel)
            if (resolveRef.current) {
                resolveRef.current(false)
                resolveRef.current = null
            }
        }
    }, [])

    /**
     * Checks if key was changed and shows confirmation dialog if needed
     * @param originalText - The original text before editing
     * @param newText - The new text after editing
     * @returns Promise that resolves to true if user confirmed or no change, false if cancelled
     */
    const checkAndConfirmKeyChange = useCallback(
        (originalText: string, newText: string): Promise<boolean> => {
            // Check if key changed
            if (!hasKeyChanged(originalText, newText)) {
                // No key change, proceed without confirmation
                return Promise.resolve(true)
            }

            // If feature flag is disabled, revert key changes (old behavior - keys not editable)
            if (!previewFeatures.hasKeyModification) {
                return Promise.resolve(false)
            }

            // Check if user has dismissed this warning for this session
            const isDismissed = sessionStorage.getItem(KEY_MODIFICATION_WARNING_DISMISSED_KEY) === 'true'
            if (isDismissed) {
                // User has opted to not show this warning, proceed without dialog
                return Promise.resolve(true)
            }

            // Key changed, show confirmation dialog
            return new Promise<boolean>((resolve) => {
                resolveRef.current = resolve

                const extractKey = (text: string) => {
                    const colonIndex = text.indexOf(':')
                    return colonIndex !== -1 ? text.substring(0, colonIndex + 1) : text
                }

                const originalKey = extractKey(originalText)
                const modifiedKey = extractKey(newText)

                showDialog({
                    isPTKey: false, // This is for base content key modification
                    originalKey,
                    modifiedKey,
                    onProceed: () => {
                        closeDialog()
                        resolveRef.current?.(true)
                        resolveRef.current = null
                    },
                    onCancel: () => {
                        closeDialog()
                        resolveRef.current?.(false)
                        resolveRef.current = null
                    },
                })
            })
        },
        [showDialog, closeDialog, previewFeatures.hasKeyModification]
    )

    return { checkAndConfirmKeyChange }
}
